/**
 * One-click handoffs that open a coding agent with the AnyAPI onboarding prompt
 * already in its composer. Every target populates the prompt without sending it,
 * which is the affordance we want: the person reads the line before their agent
 * acts on it.
 *
 * These are external contracts we do not own, so each one is cited:
 * - Claude Code: https://code.claude.com/docs/en/deep-links (`open` is the only
 *   accepted path; 5,000 char cap; the handler registers on the first
 *   interactive prompt, so an installed-but-never-used Claude Code will miss)
 * - Codex: https://learn.chatgpt.com/docs/reference/commands (5,000 char cap
 *   is not documented; the ChatGPT desktop app registers the scheme)
 * - Cursor: https://cursor.com/docs/integrations/deeplinks (8,000 char cap)
 *
 * Cursor publishes an https form, which survives browsers, proxies, and link
 * renderers that drop unknown schemes. Claude Code and Codex have no such
 * fallback, and a custom scheme with no registered handler fails *silently* -
 * no error event, nothing to feature-detect. That is why the split button
 * copies the prompt on every select: a miss degrades to "nothing opened, but
 * it is on my clipboard" instead of a dead click.
 */

export interface AgentDeeplink {
  /** Must be an id `AgentGlyph` inks, or the menu row renders no mark at all. */
  id: string;
  /** App name. Reads as "Open in {label}". */
  label: string;
  href: string;
  /**
   * https targets open a new tab so we never navigate the landing page away.
   * A custom scheme must NOT, or Chrome strands an empty about:blank behind
   * the OS handoff.
   */
  newTab: boolean;
}

/** The same three handoffs for any prompt. The onboarding CTA carries the setup
 *  line; a snippet card carries the prompt for the SKU it is showing. */
export const buildAgentDeeplinks = (prompt: string): readonly AgentDeeplink[] => {
  const q = encodeURIComponent(prompt);
  return [
    {
      id: 'claude-code',
      label: 'Claude Code',
      href: `claude-cli://open?q=${q}`,
      newTab: false,
    },
    {
      id: 'codex',
      label: 'Codex',
      href: `codex://new?prompt=${q}`,
      newTab: false,
    },
    {
      id: 'cursor',
      label: 'Cursor',
      href: `https://cursor.com/link/prompt?text=${q}`,
      newTab: true,
    },
  ];
};
